'use client'

/* eslint-disable @next/next/no-img-element */
import { cn } from '@gyym/ui'
import { useCallback, useEffect, useState } from 'react'

import { type Voice, VOICES } from '../_data'
import { ChevronLeftIcon, ChevronRightIcon } from './icons'
import { GRADIENT_PRIMARY } from './styles'

/** 自動再生の間隔 (Studio 版の実測値) */
const AUTOPLAY_INTERVAL_MS = 4000
/** スライド切り替えのアニメーション時間 */
const SLIDE_DURATION_MS = 600

const count = VOICES.length

/** active を中心 (0) とした各スライドの相対位置 (-2〜2) */
const offsetOf = (index: number, active: number) => {
  const raw = (((index - active) % count) + count) % count
  return raw > count / 2 ? raw - count : raw
}

const MultilineText = ({ lines }: { lines: string[] }) =>
  lines.map((line, i) => (
    <span key={line}>
      {i > 0 && <br />}
      {line}
    </span>
  ))

const VoiceCard = ({ voice }: { voice: Voice }) => (
  <article className="flex min-h-[655px] w-full flex-col items-center rounded-[10px] bg-white shadow-[0_2px_5px_rgba(0,0,0,0.2)] max-[540px]:min-h-[700px]">
    <div
      className={cn(
        'flex min-h-[72px] w-full flex-col items-center justify-center rounded-t-md max-[540px]:min-h-[64px]',
        GRADIENT_PRIMARY,
      )}
    >
      <p className="py-[10px] text-center text-[20px] font-medium leading-6 text-white max-[540px]:text-[18px] max-[540px]:leading-[21.6px]">
        <MultilineText lines={voice.company} />
      </p>
    </div>

    <div className="flex h-[120px] w-full items-center gap-4 px-5 max-[540px]:h-auto max-[540px]:flex-col max-[540px]:gap-0 max-[540px]:pb-5">
      <div className="flex h-[120px] w-[110px] shrink-0 items-center justify-center max-[540px]:my-[10px] max-[540px]:w-[160px]">
        <img
          src={voice.logo.src}
          alt={voice.logo.alt}
          className="size-full object-cover"
        />
      </div>
      <div className="flex flex-col items-start gap-[10px] max-[540px]:w-full">
        <div className="flex items-center gap-[10px]">
          <span className="rounded-xs border border-solid border-[#D6096C] bg-white px-[6px] py-1 text-[12px] leading-[16.8px] text-[#D6096C]">
            従業員数
          </span>
          <span className="flex items-center gap-px">
            <span className="text-[18px] leading-[18px] text-black max-[540px]:text-[16px] max-[540px]:leading-4">
              {voice.employees}
            </span>
            <span className="text-[18px] leading-[25.2px] max-[540px]:text-[16px] max-[540px]:leading-[22.4px]">
              名
            </span>
          </span>
        </div>
        <div className="flex items-center gap-[10px]">
          <span className="rounded-xs border border-solid border-[#D6096C] bg-white px-[6px] py-1 text-[12px] font-bold leading-[16.8px] text-[#D6096C]">
            業 種
          </span>
          <span className="text-[16px] leading-[19.2px] text-black">
            <MultilineText lines={voice.industry} />
          </span>
        </div>
      </div>
    </div>

    <div className="flex w-full flex-col items-start gap-5 px-6 pb-5 max-[540px]:gap-[10px]">
      <p className="text-[22px] font-medium leading-[33px] text-[#32355B] max-[540px]:text-[18px] max-[540px]:leading-[27px]">
        {voice.catchphrase}
      </p>
      <div className="h-[2px] w-full rounded-sm bg-[#D6096C]" />
      <p className="text-[16px] font-normal leading-[25.6px] text-black max-[540px]:text-[14px] max-[540px]:leading-[22.4px]">
        {voice.body}
      </p>
    </div>
  </article>
)

const ArrowButton = ({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next'
  onClick: () => void
}) => {
  const Icon = direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'prev' ? '前の声を見る' : '次の声を見る'}
      className="flex size-12 items-center justify-center rounded-full bg-[#D6096C] text-white transition-colors hover:bg-[#FF0BA4] max-[820px]:size-16 max-[540px]:size-10"
    >
      <Icon className="size-8 max-[820px]:size-9" />
    </button>
  )
}

/**
 * ご導入いただいた法人会員様の声のカルーセル。
 * 中央のスライドを active とし、前後 2 枚ずつを両脇に配置して 4 秒ごとに自動で進む。
 * マウスを乗せている間・キーボードでフォーカスしている間は止め、
 * prefers-reduced-motion: reduce のときは自動では進めない。
 */
export const VoiceCarousel = () => {
  // prev = 直前の active。端から反対側へ回り込むスライドの判定に使う
  const [{ active, prev }, setSlide] = useState({ active: 0, prev: 0 })
  // 自動再生のタイマーを手動操作でリセットするためのカウンタ
  const [autoplayKey, setAutoplayKey] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const paused = hovered || focused

  const goTo = useCallback((next: number) => {
    setSlide((s) => ({
      active: ((next % count) + count) % count,
      prev: s.active,
    }))
    setAutoplayKey((key) => key + 1)
  }, [])

  useEffect(() => {
    if (paused) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const timer = setInterval(
      () =>
        setSlide((s) => ({ active: (s.active + 1) % count, prev: s.active })),
      AUTOPLAY_INTERVAL_MS,
    )
    return () => clearInterval(timer)
  }, [autoplayKey, paused])

  const offsets = VOICES.map((_, index) => offsetOf(index, active))
  const prevOffsets = VOICES.map((_, index) => offsetOf(index, prev))

  // 代替フォントで本文が元より数行増えるため、コンテナは元 (670/740px) より少し高くしてカードを収める
  return (
    <div
      className="relative h-[710px] w-[1320px] max-w-full overflow-hidden rounded-[10px] [--slide-gap:24px] [--slide-w:390px] max-[820px]:w-full max-[540px]:h-[790px] max-[540px]:[--slide-gap:16px] max-[540px]:[--slide-w:calc(84%-2.56px)]"
      // タッチ操作のタップでは止めない (mouseenter 相当が発火したまま残るため)
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setHovered(false)}
      // マウスで矢印を押したときのフォーカスでは止めない
      onFocus={(e) => setFocused(e.target.matches(':focus-visible'))}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false)
      }}
    >
      {VOICES.map((voice, index) => {
        const offset = offsets[index] ?? 0
        // 端から反対側へ回り込むスライドはアニメーションさせない (逆走して見えるため)
        const wraps = Math.abs(offset - (prevOffsets[index] ?? offset)) > 2
        return (
          <div
            key={voice.company.join('')}
            aria-hidden={offset !== 0}
            className={cn(
              'absolute top-1/2 w-[var(--slide-w)] -translate-x-1/2 -translate-y-1/2 ease-[cubic-bezier(0.4,0.4,0,1)]',
              wraps ? 'transition-none' : 'transition-[left]',
            )}
            style={{
              left: `calc(50% + ${offset} * (var(--slide-w) + var(--slide-gap)))`,
              transitionDuration: `${SLIDE_DURATION_MS}ms`,
            }}
          >
            <VoiceCard voice={voice} />
          </div>
        )
      })}

      <div className="pointer-events-none absolute inset-x-2 top-[278px] z-[3] flex items-center justify-between max-[820px]:inset-x-[140px] max-[540px]:inset-x-1 max-[540px]:top-[340px] [&>*]:pointer-events-auto">
        <ArrowButton direction="prev" onClick={() => goTo(active - 1)} />
        <ArrowButton direction="next" onClick={() => goTo(active + 1)} />
      </div>
    </div>
  )
}
